import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, BellRing, BookMarked, ChevronDown, ChevronRight, Download, FileDown, Mail, Pencil, Plus, Receipt, RefreshCw, Trash2, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const OFFERTE_STATUSES = [
    { id: "concept", label: "Concept" },
    { id: "verstuurd", label: "Verstuurd" },
    { id: "akkoord", label: "Akkoord" },
    { id: "afgewezen", label: "Afgewezen" },
];
const FACTUUR_STATUSES = [
    { id: "concept", label: "Concept" },
    { id: "verstuurd", label: "Verstuurd" },
    { id: "betaald", label: "Betaald" },
    { id: "verlopen", label: "Verlopen" },
];

const STATUS_STYLES = {
    concept: "border-white/20 text-ash",
    verstuurd: "border-sky-400/40 text-sky-300",
    akkoord: "border-gold/60 text-gold",
    betaald: "border-gold/60 text-gold",
    afgewezen: "border-red-400/30 text-red-300/70",
    verlopen: "border-red-400/30 text-red-300/70",
};

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: "" };
const EMPTY_FORM = { doc_type: "offerte", company: "", contact_id: "", items: [{ ...EMPTY_ITEM }], vat_rate: 21, due_date: "", notes: "" };

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const statusesFor = (docType) => (docType === "offerte" ? OFFERTE_STATUSES : FACTUUR_STATUSES);
const statusLabel = (docType, id) => statusesFor(docType).find((s) => s.id === id)?.label || id;

export const InvoicingModule = () => {
    const [docs, setDocs] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [filter, setFilter] = useState("alle");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_FORM);
    const [notice, setNotice] = useState("");
    const [bk, setBk] = useState(null);
    const [showBk, setShowBk] = useState(false);
    const [bkForm, setBkForm] = useState({ provider: "moneybird", token: "", administration_id: "" });
    const [bkMsg, setBkMsg] = useState("");

    const loadBk = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/settings/bookkeeping");
            setBk(data);
        } catch {}
    }, []);

    const saveBk = async (e) => {
        e.preventDefault();
        setBkMsg("");
        try {
            await platformClient.put("/settings/bookkeeping", bkForm);
            setBkForm({ provider: bkForm.provider, token: "", administration_id: bkForm.administration_id });
            setBkMsg("Boekhouding gekoppeld");
            loadBk();
        } catch (err) {
            setBkMsg(formatPlatformError(err.response?.data?.detail));
        }
    };

    const disconnectBk = async () => {
        try {
            await platformClient.delete("/settings/bookkeeping");
            setBk({ status: "not_configured", provider: null });
            setBkMsg("Koppeling verwijderd");
        } catch {}
    };

    const syncDoc = async (doc) => {
        setError("");
        setNotice("");
        try {
            const { data } = await platformClient.post(`/invoicing/documents/${doc.id}/sync`);
            setNotice(
                data.status === "already_synced"
                    ? `${doc.number} was al gesynchroniseerd`
                    : `${doc.number} gesynchroniseerd naar ${data.provider === "moneybird" ? "Moneybird" : "e-Boekhouden"}`
            );
            setTimeout(() => setNotice(""), 6000);
            load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 409) {
                setShowBk(true);
            }
            setError(formatPlatformError(detail));
        }
    };

    const emailDoc = async (doc) => {
        setError("");
        setNotice("");
        try {
            const { data } = await platformClient.post(`/invoicing/documents/${doc.id}/email`);
            setNotice(`${doc.number} verstuurd naar ${data.to}`);
            setTimeout(() => setNotice(""), 5000);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const remindDoc = async (doc) => {
        setError("");
        setNotice("");
        try {
            const { data } = await platformClient.post(`/invoicing/documents/${doc.id}/reminder`);
            setNotice(`Herinnering voor ${doc.number} verstuurd naar ${data.to}`);
            setTimeout(() => setNotice(""), 5000);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const exportCsv = async () => {
        try {
            const res = await platformClient.get("/invoicing/export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "facturatie-export.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch {}
    };

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/invoicing/documents");
            setDocs(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
        loadBk();
        (async () => {
            try {
                const { data } = await platformClient.get("/crm/contacts");
                setContacts(data);
            } catch {}
        })();
    }, [load]);

    const counts = useMemo(
        () => ({
            alle: docs.length,
            offerte: docs.filter((d) => d.doc_type === "offerte").length,
            factuur: docs.filter((d) => d.doc_type === "factuur").length,
        }),
        [docs]
    );

    const openstaand = useMemo(
        () => docs.filter((d) => d.doc_type === "factuur" && ["concept", "verstuurd"].includes(d.status)).reduce((s, d) => s + (d.total || 0), 0),
        [docs]
    );
    const offertePipeline = useMemo(
        () => docs.filter((d) => d.doc_type === "offerte" && d.status === "verstuurd").reduce((s, d) => s + (d.total || 0), 0),
        [docs]
    );

    const visible = filter === "alle" ? docs : docs.filter((d) => d.doc_type === filter);

    const calcTotals = (items, vatRate) => {
        const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
        const vat = (subtotal * (Number(vatRate) || 0)) / 100;
        return { subtotal, vat, total: subtotal + vat };
    };

    const payloadFrom = (f) => ({
        doc_type: f.doc_type,
        company: f.company,
        contact_id: f.contact_id,
        items: f.items
            .filter((i) => i.description.trim())
            .map((i) => ({ description: i.description.trim(), quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 })),
        vat_rate: Number(f.vat_rate) || 0,
        due_date: f.due_date,
        notes: f.notes,
    });

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/invoicing/documents", payloadFrom(form));
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const patch = async (doc, update) => {
        setDocs((list) => list.map((d) => (d.id === doc.id ? { ...d, ...update } : d)));
        try {
            await platformClient.patch(`/invoicing/documents/${doc.id}`, update);
            load();
        } catch {
            load();
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const p = payloadFrom(editForm);
            delete p.doc_type;
            await patch(editing, p);
            setEditing(null);
        } finally {
            setBusy(false);
        }
    };

    const convert = async (doc) => {
        try {
            const { data } = await platformClient.post(`/invoicing/documents/${doc.id}/convert`);
            await load();
            setFilter("alle");
            setExpanded(data.id);
        } catch {}
    };

    const downloadPdf = async (doc) => {
        try {
            const res = await platformClient.get(`/invoicing/documents/${doc.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${doc.number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {}
    };

    const remove = async (doc) => {
        if (!window.confirm(`${doc.number} definitief verwijderen?`)) return;
        setDocs((list) => list.filter((d) => d.id !== doc.id));
        try {
            await platformClient.delete(`/invoicing/documents/${doc.id}`);
        } catch {
            load();
        }
    };

    const startEdit = (d) => {
        setEditing(d);
        setEditForm({
            doc_type: d.doc_type,
            company: d.company || "",
            contact_id: d.contact_id || "",
            items: (d.items || []).map((i) => ({ ...i })),
            vat_rate: d.vat_rate ?? 21,
            due_date: d.due_date || "",
            notes: d.notes || "",
        });
    };

    const pickContact = (f, setF, companyName) => {
        const match = contacts.find((c) => c.company === companyName);
        setF({ ...f, company: companyName, contact_id: match ? match.id : "" });
    };

    const DocForm = ({ values, setValues, onSubmit, submitLabel, idPrefix }) => {
        const t = calcTotals(values.items, values.vat_rate);
        const setItem = (idx, key, val) =>
            setValues({ ...values, items: values.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) });
        return (
            <form onSubmit={onSubmit} className="mt-6 space-y-6">
                {!idPrefix.includes("edit") ? (
                    <div className="flex gap-2">
                        {["offerte", "factuur"].map((tp) => (
                            <button key={tp} type="button" data-testid={`inv-form-type-${tp}`}
                                onClick={() => setValues({ ...values, doc_type: tp })}
                                className={`border px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                                    values.doc_type === tp ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-ash hover:border-gold/40"
                                }`}>
                                {tp === "offerte" ? "Offerte" : "Factuur"}
                            </button>
                        ))}
                    </div>
                ) : null}
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                        <label className={labelCls} htmlFor={`${idPrefix}-company`}>Klant / bedrijf *</label>
                        <input id={`${idPrefix}-company`} data-testid={`${idPrefix}-company`} required className={inputCls}
                            value={values.company} onChange={(e) => pickContact(values, setValues, e.target.value)}
                            list={`${idPrefix}-contacts`} placeholder="Bedrijfsnaam" />
                        <datalist id={`${idPrefix}-contacts`}>
                            {contacts.map((c) => (
                                <option key={c.id} value={c.company} />
                            ))}
                        </datalist>
                    </div>
                    <div>
                        <label className={labelCls} htmlFor={`${idPrefix}-due`}>Vervaldatum</label>
                        <input id={`${idPrefix}-due`} data-testid={`${idPrefix}-due`} type="date" className={inputCls}
                            value={values.due_date} onChange={(e) => setValues({ ...values, due_date: e.target.value })} />
                    </div>
                </div>
                <div>
                    <p className={labelCls}>Regels</p>
                    <div className="mt-3 space-y-3">
                        {values.items.map((item, idx) => (
                            <div key={idx} className="flex flex-wrap items-end gap-3">
                                <input data-testid={`${idPrefix}-item-desc-${idx}`} className={`${inputCls} flex-1 min-w-[200px]`}
                                    value={item.description} onChange={(e) => setItem(idx, "description", e.target.value)}
                                    placeholder="Omschrijving" />
                                <input data-testid={`${idPrefix}-item-qty-${idx}`} type="number" min="0" step="any"
                                    className={`${inputCls} w-24`} value={item.quantity}
                                    onChange={(e) => setItem(idx, "quantity", e.target.value)} placeholder="Aantal" />
                                <input data-testid={`${idPrefix}-item-price-${idx}`} type="number" min="0" step="any"
                                    className={`${inputCls} w-32`} value={item.unit_price}
                                    onChange={(e) => setItem(idx, "unit_price", e.target.value)} placeholder="Prijs p/st" />
                                {values.items.length > 1 ? (
                                    <button type="button" data-testid={`${idPrefix}-item-del-${idx}`}
                                        onClick={() => setValues({ ...values, items: values.items.filter((_, i) => i !== idx) })}
                                        className="pb-2.5 text-ash transition-colors hover:text-red-300">
                                        <X className="h-4 w-4" />
                                    </button>
                                ) : null}
                            </div>
                        ))}
                    </div>
                    <button type="button" data-testid={`${idPrefix}-item-add`}
                        onClick={() => setValues({ ...values, items: [...values.items, { ...EMPTY_ITEM }] })}
                        className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-bone">
                        <Plus className="h-3.5 w-3.5" /> Regel toevoegen
                    </button>
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                        <label className={labelCls} htmlFor={`${idPrefix}-vat`}>BTW %</label>
                        <input id={`${idPrefix}-vat`} data-testid={`${idPrefix}-vat`} type="number" min="0" max="100" step="any"
                            className={inputCls} value={values.vat_rate}
                            onChange={(e) => setValues({ ...values, vat_rate: e.target.value })} />
                    </div>
                    <div data-testid={`${idPrefix}-totals`} className="self-end border border-white/10 bg-ink p-4 text-sm">
                        <p className="flex justify-between text-ash"><span>Subtotaal</span><span>{euro(t.subtotal)}</span></p>
                        <p className="mt-1 flex justify-between text-ash"><span>BTW</span><span>{euro(t.vat)}</span></p>
                        <p className="mt-2 flex justify-between border-t border-white/10 pt-2 text-gold"><span>Totaal</span><span>{euro(t.total)}</span></p>
                    </div>
                </div>
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-notes`}>Notities</label>
                    <textarea id={`${idPrefix}-notes`} data-testid={`${idPrefix}-notes`} rows={2} className={`${inputCls} resize-none`}
                        value={values.notes} onChange={(e) => setValues({ ...values, notes: e.target.value })}
                        placeholder="Betalingstermijn, referenties…" />
                </div>
                {error ? <p data-testid={`${idPrefix}-error`} className="text-xs text-red-400">{error}</p> : null}
                <button data-testid={`${idPrefix}-submit`} disabled={busy}
                    className="border border-gold bg-gold px-8 py-3 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                    {busy ? "Bezig…" : submitLabel}
                </button>
            </form>
        );
    };

    return (
        <div data-testid="invoicing-module" className="mt-10">
            <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Facturatie</p>
                    <h2 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                        Offertes &amp; <span className="italic text-gold">facturen</span>
                    </h2>
                    <p data-testid="inv-summary" className="mt-3 text-sm text-ash">
                        Openstaand: <span className="text-gold">{euro(openstaand)}</span>
                        <span className="mx-2 text-white/20">·</span>
                        Offertes in de markt: <span className="text-gold">{euro(offertePipeline)}</span>
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button data-testid="inv-export-csv" onClick={exportCsv}
                        className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                        <Download className="h-4 w-4" /> Export
                    </button>
                    <button data-testid="inv-bookkeeping-toggle" onClick={() => setShowBk((v) => !v)}
                        className={`flex items-center gap-2 border px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
                            bk?.status === "connected"
                                ? "border-gold/40 bg-gold/5 text-gold hover:bg-gold hover:text-black"
                                : "border-white/15 text-ash hover:border-gold/50 hover:text-gold"
                        }`}>
                        <BookMarked className="h-4 w-4" />
                        {bk?.status === "connected" ? (bk.provider === "moneybird" ? "Moneybird gekoppeld" : "e-Boekhouden gekoppeld") : "Boekhouding"}
                    </button>
                    <button data-testid="inv-add-button" onClick={() => setShowForm((v) => !v)}
                        className="flex items-center gap-2 border border-gold/50 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                        {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                        {showForm ? "Sluiten" : "Nieuw document"}
                    </button>
                </div>
            </div>

            {showForm ? (
                <div data-testid="inv-create-panel" className="mt-8 border border-white/10 bg-coal p-8">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Nieuw document</p>
                    <DocForm values={form} setValues={setForm} onSubmit={create} submitLabel="Aanmaken" idPrefix="inv-form" />
                </div>
            ) : null}

            {showBk ? (
                <div data-testid="bookkeeping-panel" className="mt-8 border border-white/10 bg-coal p-8">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Boekhouding koppelen</p>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-ash">
                        Verstuurde facturen gaan met één klik naar je boekhoudpakket. Je API-token wordt
                        versleuteld opgeslagen en nooit getoond.
                    </p>
                    {bk?.status === "connected" ? (
                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            <p data-testid="bk-status" className="border border-gold/40 bg-gold/10 px-4 py-2.5 text-xs uppercase tracking-[0.15em] text-gold">
                                Gekoppeld aan {bk.provider === "moneybird" ? "Moneybird" : "e-Boekhouden"}
                                {bk.administration_id ? ` (adm. ${bk.administration_id})` : ""}
                            </p>
                            <button data-testid="bk-disconnect" onClick={disconnectBk}
                                className="border border-white/15 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-red-400/50 hover:text-red-300">
                                Koppeling verwijderen
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={saveBk} className="mt-6 grid max-w-xl grid-cols-1 gap-5">
                            <div>
                                <label className={labelCls} htmlFor="bk-provider">Pakket</label>
                                <select id="bk-provider" data-testid="bk-provider" className={`${inputCls} bg-ink`} value={bkForm.provider}
                                    onChange={(e) => setBkForm({ ...bkForm, provider: e.target.value })}>
                                    <option value="moneybird">Moneybird</option>
                                    <option value="eboekhouden">e-Boekhouden.nl</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="bk-token">API-token</label>
                                <input id="bk-token" data-testid="bk-token" type="password" required minLength={8} className={inputCls}
                                    value={bkForm.token} onChange={(e) => setBkForm({ ...bkForm, token: e.target.value })}
                                    placeholder="Plak je API-token" />
                            </div>
                            {bkForm.provider === "moneybird" ? (
                                <div>
                                    <label className={labelCls} htmlFor="bk-admin">Administratie-ID</label>
                                    <input id="bk-admin" data-testid="bk-admin" required className={inputCls}
                                        value={bkForm.administration_id} onChange={(e) => setBkForm({ ...bkForm, administration_id: e.target.value })}
                                        placeholder="Te vinden in Moneybird onder Instellingen" />
                                </div>
                            ) : null}
                            <button data-testid="bk-save"
                                className="w-fit border border-gold bg-gold px-8 py-3 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-transparent hover:text-gold">
                                Koppelen
                            </button>
                        </form>
                    )}
                    {bkMsg ? <p data-testid="bk-message" className="mt-4 text-xs text-gold">{bkMsg}</p> : null}
                </div>
            ) : null}

            <div data-testid="inv-filters" className="mt-10 flex flex-wrap gap-2">
                {[{ id: "alle", label: "Alle" }, { id: "offerte", label: "Offertes" }, { id: "factuur", label: "Facturen" }].map((f) => (
                    <button key={f.id} data-testid={`inv-filter-${f.id}`} onClick={() => setFilter(f.id)}
                        className={`border px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                            filter === f.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-ash hover:border-gold/40 hover:text-bone"
                        }`}>
                        {f.label} ({counts[f.id] ?? 0})
                    </button>
                ))}
            </div>

            {notice ? (
                <p data-testid="inv-notice" className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 text-xs uppercase tracking-[0.15em] text-gold">
                    {notice}
                </p>
            ) : null}
            {error && !showForm && !editing ? (
                <p data-testid="inv-error" className="mt-4 border border-red-400/30 bg-red-400/5 px-4 py-3 text-xs text-red-300">
                    {error}
                </p>
            ) : null}

            <div className="mt-6 space-y-3">
                {visible.length === 0 ? (
                    <p data-testid="inv-empty" className="border border-white/10 bg-coal p-10 text-sm text-ash">
                        Nog geen documenten in dit overzicht.
                    </p>
                ) : (
                    visible.map((d) => {
                        const open = expanded === d.id;
                        return (
                            <div key={d.id} data-testid={`inv-doc-${d.id}`} className="border border-white/10 bg-coal">
                                <button data-testid={`inv-doc-toggle-${d.id}`} onClick={() => setExpanded(open ? null : d.id)}
                                    className="flex w-full flex-wrap items-center gap-4 p-5 text-left">
                                    {open ? <ChevronDown className="h-4 w-4 text-gold" /> : <ChevronRight className="h-4 w-4 text-ash" />}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-display text-lg tracking-tight">{d.company}</p>
                                        <p className="text-xs text-ash">
                                            {d.number}
                                            {d.from_offerte ? <span className="text-gold/70"> · van {d.from_offerte}</span> : null}
                                        </p>
                                    </div>
                                    {d.due_date ? (
                                        <span className="text-[10px] uppercase tracking-[0.15em] text-ash">Verval: {d.due_date}</span>
                                    ) : null}
                                    <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[d.status] || STATUS_STYLES.concept}`}>
                                        {d.doc_type === "offerte" ? "Offerte" : "Factuur"} · {statusLabel(d.doc_type, d.status)}
                                    </span>
                                    <span className="text-sm text-gold">{euro(d.total)}</span>
                                </button>

                                {open ? (
                                    <div className="border-t border-white/10 p-6">
                                        {editing?.id === d.id ? (
                                            <DocForm values={editForm} setValues={setEditForm} onSubmit={saveEdit}
                                                submitLabel="Opslaan" idPrefix={`inv-edit-${d.id}`} />
                                        ) : (
                                            <>
                                                <table className="w-full text-sm">
                                                    <tbody>
                                                        {(d.items || []).map((item, i) => (
                                                            <tr key={i} className="border-b border-white/5">
                                                                <td className="py-2 text-bone">{item.description}</td>
                                                                <td className="py-2 text-right text-ash">{item.quantity} ×</td>
                                                                <td className="py-2 text-right text-ash">{euro(item.unit_price)}</td>
                                                                <td className="py-2 text-right text-bone">{euro(item.quantity * item.unit_price)}</td>
                                                            </tr>
                                                        ))}
                                                        <tr>
                                                            <td colSpan={3} className="pt-3 text-right text-xs uppercase tracking-[0.15em] text-ash">Subtotaal</td>
                                                            <td className="pt-3 text-right text-ash">{euro(d.subtotal)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan={3} className="text-right text-xs uppercase tracking-[0.15em] text-ash">BTW {d.vat_rate}%</td>
                                                            <td className="text-right text-ash">{euro(d.vat_amount)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan={3} className="pt-2 text-right text-xs uppercase tracking-[0.15em] text-gold">Totaal</td>
                                                            <td data-testid={`inv-total-${d.id}`} className="pt-2 text-right font-display text-lg text-gold">{euro(d.total)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                {d.notes ? <p className="mt-4 text-xs leading-relaxed text-ash">{d.notes}</p> : null}
                                                <div className="mt-5 flex flex-wrap gap-2">
                                                    {statusesFor(d.doc_type).map((s) => (
                                                        <button key={s.id} data-testid={`inv-set-status-${d.id}-${s.id}`}
                                                            onClick={() => patch(d, { status: s.id })}
                                                            className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                                                                d.status === s.id ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-ash hover:border-gold/40"
                                                            }`}>
                                                            {s.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="mt-4 flex flex-wrap gap-3">
                                                    {d.doc_type === "offerte" ? (
                                                        <button data-testid={`inv-convert-${d.id}`} onClick={() => convert(d)}
                                                            className="flex items-center gap-2 border border-gold/50 bg-gold/5 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                                                            <ArrowRight className="h-3 w-3" /> Omzetten naar factuur
                                                        </button>
                                                    ) : null}
                                                    <button data-testid={`inv-pdf-${d.id}`} onClick={() => downloadPdf(d)}
                                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                        <FileDown className="h-3 w-3" /> PDF
                                                    </button>
                                                    <button data-testid={`inv-email-${d.id}`} onClick={() => emailDoc(d)}
                                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                        <Mail className="h-3 w-3" /> E-mail
                                                    </button>
                                                    {d.doc_type === "factuur" && ["verstuurd", "verlopen"].includes(d.status) ? (
                                                        <button data-testid={`inv-remind-${d.id}`} onClick={() => remindDoc(d)}
                                                            className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-amber-400/50 hover:text-amber-300">
                                                            <BellRing className="h-3 w-3" /> Herinnering
                                                        </button>
                                                    ) : null}
                                                    {d.doc_type === "factuur" ? (
                                                        d.synced_provider ? (
                                                            <span data-testid={`inv-synced-${d.id}`} className="flex items-center gap-2 border border-gold/40 bg-gold/10 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold">
                                                                <RefreshCw className="h-3 w-3" /> In {d.synced_provider === "moneybird" ? "Moneybird" : "e-Boekhouden"}
                                                            </span>
                                                        ) : (
                                                            <button data-testid={`inv-sync-${d.id}`} onClick={() => syncDoc(d)}
                                                                className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                                <RefreshCw className="h-3 w-3" /> Boekhouding
                                                            </button>
                                                        )
                                                    ) : null}
                                                    <button data-testid={`inv-edit-${d.id}`} onClick={() => startEdit(d)}
                                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                                                        <Pencil className="h-3 w-3" /> Bewerken
                                                    </button>
                                                    <button data-testid={`inv-delete-${d.id}`} onClick={() => remove(d)}
                                                        className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-red-400/50 hover:text-red-300">
                                                        <Trash2 className="h-3 w-3" /> Verwijderen
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
